import { Form } from "@unform/web";
import { HandCoins } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import Preview from "~/components/Preview";
import FormHeader from "~/components/RegisterForm/FormHeader";
import FormLoading from "~/components/RegisterForm/FormLoading";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import DatePicker from "~/components/RegisterForm/DatePicker";
import Textarea from "~/components/RegisterForm/Textarea";
import api from "~/services/api";
import { FullGridContext } from "../..";

export const InputContext = createContext({});

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function invoiceLabel(receivable) {
  const invoiceNumber = receivable.invoice_number
    ?.toString()
    .padStart(6, "0");
  return `#${invoiceNumber || "-"}`;
}

function activeResolution(receivable, today) {
  return receivable.financialHoldResolutions?.find(
    (resolution) => resolution.grace_until >= today
  );
}

export default function ResolveIssue({
  access,
  selected,
  handleOpened,
  defaultFormType = "full",
}) {
  const { successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    student: null,
    receivables: [],
    loaded: false,
  });
  const [selectedReceivableId, setSelectedReceivableId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const generalForm = useRef();
  const studentId = selected?.[0]?.id;
  const today = format(new Date(), "yyyy-MM-dd");

  function handleCloseForm() {
    handleOpened(null);
  }

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const { data } = await api.get(
          `/students/${studentId}/overdue-receivables`
        );
        if (!mounted) return;

        const receivables = data.receivables || [];
        const firstAvailable = receivables.find(
          (receivable) => !activeResolution(receivable, today)
        );
        setPageData({
          student: data.student,
          receivables,
          loaded: true,
        });
        setSelectedReceivableId(firstAvailable?.id || null);
      } catch (err) {
        toast(
          err?.response?.data?.error || "Failed to load overdue invoices.",
          { type: "error", autoClose: 3000 }
        );
        handleOpened(null);
      }
    }

    if (studentId) {
      loadData();
    }

    return () => {
      mounted = false;
    };
  }, [studentId]);

  async function handleSubmit(data) {
    const memo = data.memo?.trim();
    const graceUntil = data.grace_until;

    if (!selectedReceivableId) {
      toast("Select an invoice.", { type: "warning", autoClose: 2500 });
      return;
    }
    if (!memo) {
      toast("Resolution memo is required.", {
        type: "warning",
        autoClose: 2500,
      });
      return;
    }
    if (!graceUntil) {
      toast("Payment promise date is required.", {
        type: "warning",
        autoClose: 2500,
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: response } = await api.post(
        `/students/${studentId}/financial-holds/resolve`,
        {
          receivable_id: selectedReceivableId,
          memo,
          grace_until: format(graceUntil, "yyyy-MM-dd"),
        }
      );

      toast(
        response.invoice_problem
          ? "Payment promise saved. Other invoices still keep this financial hold active."
          : "Financial hold resolved and payment promise saved.",
        {
          type: response.invoice_problem ? "warning" : "success",
          autoClose: 3500,
        }
      );
      setSuccessfullyUpdated(true);
      handleOpened(null);
    } catch (err) {
      toast(
        err?.response?.data?.error || "Failed to resolve financial hold.",
        { type: "error", autoClose: 3000 }
      );
    } finally {
      setSubmitting(false);
    }
  }

  const studentName = pageData.student
    ? `${pageData.student.name} ${pageData.student.last_name}`
    : "";

  return (
    <Preview formType={defaultFormType} fullscreen={fullscreen}>
      {pageData.loaded ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex w-32 flex-col items-center justify-between gap-4 text-xs">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <HandCoins size={16} /> Resolution
            </RegisterFormMenu>
          </div>
          <div className="flex h-full flex-1 flex-col justify-start overflow-hidden rounded-xl border">
            <div className="flex flex-col items-start justify-start overflow-y-scroll text-sm">
              <Form
                ref={generalForm}
                onSubmit={handleSubmit}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id: studentId,
                    generalForm,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    setSuccessfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  <FormHeader
                    access={access}
                    title={`Resolve Financial Hold - ${studentName}`}
                    registry={{}}
                    InputContext={InputContext}
                    loading={submitting}
                    saveText="Resolve Hold"
                    saveIcon="ShieldCheck"
                  />

                  <InputLineGroup
                    title="FINANCIAL HOLD RESOLUTION"
                    activeMenu={activeMenu === "general"}
                  >
                    <InputLine title="Overdue Invoice">
                      <div className="w-full overflow-x-auto rounded-lg border">
                        <table className="w-full border-collapse text-xs">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="w-10 px-2 py-2" />
                              <th className="px-2 py-2 text-left">Invoice</th>
                              <th className="px-2 py-2 text-left">Type</th>
                              <th className="px-2 py-2 text-left">Due Date</th>
                              <th className="px-2 py-2 text-right">Balance</th>
                              <th className="px-2 py-2 text-left">Hold Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageData.receivables.map((receivable) => {
                              const resolution = activeResolution(
                                receivable,
                                today
                              );
                              const disabled = Boolean(resolution);

                              return (
                                <tr
                                  key={receivable.id}
                                  className={`border-t ${
                                    selectedReceivableId === receivable.id
                                      ? "bg-orange-50"
                                      : "even:bg-gray-50"
                                  } ${disabled ? "text-gray-400" : ""}`}
                                >
                                  <td className="px-2 py-2 text-center">
                                    <input
                                      type="radio"
                                      name="receivable_selection"
                                      value={receivable.id}
                                      checked={
                                        selectedReceivableId === receivable.id
                                      }
                                      disabled={disabled}
                                      onChange={() => {
                                        setSelectedReceivableId(receivable.id);
                                        setSuccessfullyUpdated(false);
                                      }}
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    {invoiceLabel(receivable)}
                                  </td>
                                  <td className="px-2 py-2">
                                    {receivable.type_detail || receivable.type}
                                  </td>
                                  <td className="px-2 py-2">
                                    {receivable.due_date}
                                  </td>
                                  <td className="px-2 py-2 text-right">
                                    {formatCurrency(receivable.balance)}
                                  </td>
                                  <td className="px-2 py-2">
                                    {resolution
                                      ? `Promise active through ${resolution.grace_until}`
                                      : "Blocking"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </InputLine>

                    {pageData.receivables.length === 0 && (
                      <p className="px-4 text-xs text-gray-500">
                        No overdue invoices are available for resolution.
                      </p>
                    )}
                    {pageData.receivables.length > 0 &&
                      !selectedReceivableId && (
                        <p className="px-4 text-xs text-green-700">
                          All overdue invoices currently have active payment
                          promises. The student remains visible here for
                          collection follow-up.
                        </p>
                      )}

                    <InputLine title="Payment Agreement">
                      <DatePicker
                        name="grace_until"
                        title="Payment Promise Date"
                        defaultValue={new Date()}
                        minDate={new Date()}
                        placeholderText="MM/DD/YYYY"
                        grow
                        required
                        disabled={submitting}
                        InputContext={InputContext}
                      />
                    </InputLine>
                    <InputLine title="Resolution Memo">
                      <Textarea
                        name="memo"
                        title="Memo"
                        rows={4}
                        maxLength={5000}
                        grow
                        required
                        disabled={submitting}
                        allowAccents
                        InputContext={InputContext}
                      />
                    </InputLine>
                    <p className="px-4 pb-4 text-xs text-gray-500">
                      The selected invoice will not count toward the financial
                      hold through the promise date. If it remains unpaid, it
                      will count again on the following day.
                    </p>
                  </InputLineGroup>
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : (
        <FormLoading />
      )}
    </Preview>
  );
}
